/**
 * ActiveWorkers — migrated from mockData to live svdy-platform API.
 *
 * Phase A per HANDOFF § 11 row 12: list + basic form with real FK
 * Selects for department / position / employment_type / location /
 * manager. Phase B (filters, CSV import, transfer) is deferred.
 *
 * Filter contract: this page shows workers with status === 'active'
 * (or no status — backend defaults to active). The sibling
 * FormerWorkers page is a thin derivative that filters on
 * status === 'terminated' and is otherwise the same code shape.
 *
 * FK Selects are populated from parallel list calls on mount so the
 * dropdowns stay in lock-step with the rest of the Organization
 * module.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import VpnKeyOutlined from '@mui/icons-material/VpnKeyOutlined';
import VpnKeyOffOutlinedIcon from '@mui/icons-material/VpnKeyOffOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditIcon from '@mui/icons-material/Edit';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ApiError,
  attendanceApi,
  organizationApi,
  systemApi,
  type Schema,
} from '../../../api';
import { translateApiError } from '../../../i18n/translateApiError';
import { ConfirmDialog } from '../../../components/primitives';
import UpsellDialog, { useUpsellOnLimit } from '../../../components/billing/UpsellDialog';
import PhoneField from '../../../components/forms/PhoneField';

import DateField from '../../../components/inputs/DateField';
import WorkerDocumentsDialog from '../../../components/organization/WorkerDocumentsDialog';
import type { RequiredDocumentSummary } from '../../../api/organization';
import { ImportDialog } from '../../../components/import/ImportDialog';
import { activeLocale } from '../../../utils/datetime';
import { FIELD_TEXT_INSET } from '../../../theme/shellMetrics';
type Worker = Schema<'WorkerResponse'>;
type WorkerUpdate = Schema<'WorkerUpdate'>;
type Department = Schema<'DepartmentResponse'>;
type Position = Schema<'PositionResponse'>;
type EmploymentType = Schema<'EmploymentTypeResponse'>;
type Location = Schema<'LocationResponse'>;
type Timetable = Schema<'TimetableResponse'>;
type ShiftCycle = Schema<'ShiftCycleResponse'>;
type ManualEntryCreate = Schema<'ManualEntryCreate'>;
type WorkerScheduleAssign = Schema<'WorkerScheduleAssign'>;

type FormState = Partial<Worker>;

const STATUS_ACTIVE = 'active';
/** ADR-049 — the employment states in which a RECORD may be removed. `terminated` because the
 *  employment fact is settled; `invited` because there is none to settle. Mirrors the backend's
 *  `DELETABLE_WORKER_STATUSES`, which returns 400 for anything else, so the UI does not offer a
 *  click the server will refuse. */
const DELETABLE_STATUSES: readonly string[] = ['terminated', 'invited'];

const ActiveWorkers: React.FC = () => {
  const { t } = useTranslation();
  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------

  const [workers, setWorkers] = useState<Worker[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<EmploymentType[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [roles, setRoles] = useState<{ id: string; code: string; name: string }[]>([]);
  // P1.2 Phase B — Add Worker default IDs from /v1/system/settings/workers.
  // Pre-fills the form when handleOpenAddDialog runs so an admin
  // doesn't re-pick the same department / position / location /
  // employment type for every invite. Loaded alongside the other
  // catalog data in the main fetchAll Promise.all so we never paint
  // a partial form.
  const [workerDefaults, setWorkerDefaults] = useState<{
    default_department_id?: string | null;
    default_position_id?: string | null;
    default_location_id?: string | null;
    default_employment_type_id?: string | null;
  }>({});
  // P1.2 Phase B item 6 — required document types (is_required=true)
  // surfaced in the Add Worker dialog as file-input rows. The dialog
  // collects the files in `docFiles`; submit chains an upload to
  // /v1/organization/workers/{id}/documents per file after the worker
  // row is created. Failures don't block the dialog close — the
  // worker still exists and admins can finish uploads from the
  // worker's profile.
  const [requiredDocTypes, setRequiredDocTypes] = useState<
    Array<{ id: string; name: string; description?: string | null }>
  >([]);
  // Non-required document types — shown in the same Add-Worker panel
  // so admins can optionally upload them now too (closing the loop on
  // 'why can I only see required ones?'). Marked 'Optional'; never block submit.
  const [optionalDocTypes, setOptionalDocTypes] = useState<
    Array<{ id: string; name: string; description?: string | null }>
  >([]);
  const [docFiles, setDocFiles] = useState<Record<string, File | null>>({});
  // super_admin is intentionally NOT exposed in any tenant-facing
  // role picker (Add Worker, Edit Worker). Per the security model it
  // can only be assigned via direct DB seed / SQL — a tenant admin
  // who could grant it via the UI would defeat the whole separation.
  //
  // Edit-mode caveat: if a worker is already super_admin (set via
  // terminal earlier), the role select must still RENDER super_admin
  // so the admin can see the truth — but they can't pick it for a
  // different worker because the option is filtered out everywhere
  // else. Encoded inline at each <Select> via:
  //   roles.filter(r => r.code !== 'super_admin' || r.id === selectedRoleId)
  const [roleByWorker, setRoleByWorker] = useState<Map<string, string>>(new Map());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Phase C — 402 limit-reached upsell (PRODUCT_SUBSCRIPTION_STRATEGY §6).
  // Triggered from save / invite paths when the platform's
  // enforce_worker_limit returns 402.
  const { upsell, openIfLimit, closeUpsell } = useUpsellOnLimit();

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [current, setCurrent] = useState<FormState | null>(null);
  // HANDOFF_2026-05-25 Bug 10 — email-change verification dialog open state.
  const [emailChangeOpen, setEmailChangeOpen] = useState(false);
  const [emailChangeNew, setEmailChangeNew] = useState('');
  const [emailChangeError, setEmailChangeError] = useState<string | null>(null);
  const [emailChangeSubmitting, setEmailChangeSubmitting] = useState(false);
  const [emailChangeSuccess, setEmailChangeSuccess] = useState(false);
  const [toDelete, setToDelete] = useState<Worker | null>(null);
  // Pending terminate target — drives the ConfirmDialog below, replaces
  // the native window.confirm() we used to call on the row icon click.
  const [pendingTerminate, setPendingTerminate] = useState<Worker | null>(
    null,
  );
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // ------------------------------------------------------------------
  // Role + login-access state (HANDOFF §21.3 — 方向 C Phase 2/3).
  //   - grantLogin       : Create-mode checkbox. When checked we route
  //                        through systemWorkersApi.invite() instead of
  //                        organizationApi.workersApi.create(), so the
  //                        new worker gets a role + invite token in
  //                        one shot.
  //   - selectedRoleId   : Dialog-local role binding. In Edit mode it's
  //                        pre-seeded from the worker's current role;
  //                        in Create+grant mode it's what the invite
  //                        request sends as role_id.
  //   - initialRoleId    : The role on dialog open. Used to detect
  //                        "role changed" so we only hit the role API
  //                        when the admin actually changed something.
  //   - inviteResult     : Post-invite dialog state — surfaces the
  //                        invite_url so admin can copy-share it as
  //                        a backup. Backend also fires the invitation
  //                        email via svdy-authorization SES.
  //   - pendingRevoke    : Confirm target for the Revoke Login button.
  // ------------------------------------------------------------------
  // grantLogin state value isn't read in JSX (legacy checkbox removed)
  // but setGrantLogin is still called from open/close handlers as a
  // future-proof scaffold. Prefix with _ so eslint stops flagging.
  const [_grantLogin, setGrantLogin] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [initialRoleId, setInitialRoleId] = useState<string>('');
  const [inviteResult, setInviteResult] = useState<{
    workerName: string;
    email: string;
    invite_url: string;
    expires_at: string;
  } | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<Worker | null>(null);
  const [revokeSaving, setRevokeSaving] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [importOpen, setImportOpen] = useState<boolean>(false);
  /**
   * ADR-044 F2 / S4 — the required-document number, driven by the THREE STATES and
   * never by the boolean.
   *
   * The boolean this replaces reported "2 outstanding" for a production org whose
   * truth was 1 overdue, 1 pending, 1 satisfied — one of the two was a July hire
   * still inside their window and not a failure at all. So `overdue` is the number
   * that goes next to the others, and `pending` rides along in the tooltip: an
   * obligation nobody can see is not an obligation, and a number that counts
   * not-yet-due people as failures is one the admin learns to ignore.
   */
  const [docSummary, setDocSummary] = useState<RequiredDocumentSummary | null>(null);
  const [deptFilter, setDeptFilter] = useState<string>('all');
  const [locFilter, setLocFilter] = useState<string>('all');

  // ------------------------------------------------------------------
  // Row action menu + attendance-linked dialogs. Replaces the two-icon
  // "Edit + Delete" tail column with a kebab that hosts the full set of
  // cross-module shortcuts — view attendance / manual entry / assign
  // schedule / transfer dept. Edit / Terminate / Delete stay inline.
  // ------------------------------------------------------------------

  const navigate = useNavigate();

  // Deep-link filter: Departments kebab lands here with
  // ?department_id=X; we strictly filter primary_department_id against
  // it so one department's roster surfaces in isolation. Clearing the
  // chip wipes the param + restores the full list.
  const [searchParams, setSearchParams] = useSearchParams();
  const deptIdFilter = searchParams.get('department_id');
  /**
   * Pre-seeds the search box, so a drill-through lands ON the person.
   *
   * Added 2026-08-10: the Today's-birthdays widget was navigating here with
   * `?worker_id=<id>`, and this page only ever read `department_id` — so the parameter
   * was silently ignored and the click landed on the full worker list with nothing
   * pointing at the person clicked. Nothing failed; the route exists and the page
   * renders (svdy-ui §12.10's family).
   *
   * `worker` carrying a NAME rather than an id, because that is the parameter Time
   * entries already accepts from two other surfaces and this filter is a text search.
   * One convention across the app instead of a third one here.
   */
  const urlWorker = searchParams.get('worker') ?? '';

  // Applied once, from the URL. An effect rather than the state initialiser because
  // `filterText` is declared above this point; a `useRef` latch is not needed since the
  // dependency is the URL value itself, which only changes when a new link is followed.
  useEffect(() => {
    if (urlWorker) setFilterText(urlWorker);
  }, [urlWorker]);

  const clearDeptIdFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('department_id');
    setSearchParams(next, { replace: true });
  };

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<Worker | null>(null);

  // Per-worker documents dialog (view / download / upload / remove a
  // single worker's files). Opened from the row kebab and from the
  // Edit dialog's documents panel. Reuses the document-type catalog
  // (required + optional) loaded in fetchAll for type labels + the
  // upload type picker — no extra fetch.
  const [docsWorker, setDocsWorker] = useState<Worker | null>(null);
  const docTypeCatalog = useMemo(
    () => [...requiredDocTypes, ...optionalDocTypes].map((d) => ({ id: d.id, name: d.name })),
    [requiredDocTypes, optionalDocTypes],
  );

  // Attendance reference data for the schedule-assignment dropdown.
  // Fetched on mount alongside the main data.
  const [timetables, setTimetables] = useState<Timetable[]>([]);
  const [shiftCycles, setShiftCycles] = useState<ShiftCycle[]>([]);

  // Manual-entry dialog state (same contract as TimeEntries, scoped to
  // the selected worker).
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualEmp, setManualEmp] = useState<Worker | null>(null);
  const [manual, setManual] = useState({
    date: new Date().toISOString().slice(0, 10),
    clock_in_time: '09:00',
    clock_out_time: '',
    notes: '',
  });

  // Schedule-assign dialog state.
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignEmp, setAssignEmp] = useState<Worker | null>(null);
  const [assignForm, setAssignForm] = useState<{
    kind: 'fixed' | 'cycle';
    timetable_id: string;
    shift_cycle_id: string;
    effective_from: string;
    auto_shift: boolean;
  }>({
    kind: 'fixed',
    timetable_id: '',
    shift_cycle_id: '',
    effective_from: new Date().toISOString().slice(0, 10),
    auto_shift: false,
  });

  // Transfer-department dialog state.
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferSaving, setTransferSaving] = useState(false);
  const [transferEmp, setTransferEmp] = useState<Worker | null>(null);
  const [transferDept, setTransferDept] = useState('');

  // Bulk transfer dialog state.
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  const [bulkTransferDept, setBulkTransferDept] = useState('');
  const [bulkTransferSaving, setBulkTransferSaving] = useState(false);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);

  // ------------------------------------------------------------------
  // Data loading
  // ------------------------------------------------------------------

  const fetchDocSummary = useCallback(async () => {
    try {
      setDocSummary(await organizationApi.requiredDocumentsApi.summary());
    } catch {
      // Context, not the page's subject. A tenant with no required types, or a
      // permission the role lacks, must not cost them the worker list.
      setDocSummary(null);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch active + invited together. 'invited' is the pre-claim
      // state for a worker who got an invite link but hasn't accepted
      // yet — they belong on this page so admins can see who's stuck
      // and click "Retry grant" to resend the invite. Only terminated
      // and on_leave workers are filtered out (they have their own
      // pages / surfaces).
      const [activeWorkers, invitedWorkers, deps, poss, types, locs, tts, cycs, rolesList, sysUsers, workerDefaultsRes, docTypesRes] = await Promise.all([
        organizationApi.workersApi.list(STATUS_ACTIVE),
        organizationApi.workersApi.list('invited'),
        organizationApi.departmentsApi.list(),
        organizationApi.positionsApi.list(),
        organizationApi.employmentTypesApi.list(),
        organizationApi.locationsApi.list(),
        // Bug fix 2026-05-22 — wrap attendance fetches in catch so an
        // org without qutime license (e.g. pureoa-only Beta) can still
        // open the Active Workers page. Pre-fix Promise.all rejected on
        // the first 403 license_required → user saw 'QuTime not in your
        // plan' on a shared module page (Organization). The schedule-
        // assignment dropdown just renders empty when these come back
        // as []; admin sees "no timetables" rather than a broken page.
        attendanceApi.timetablesApi.list().catch(() => []),
        attendanceApi.shiftCyclesApi.list().catch(() => []),
        systemApi.systemRolesApi.list() as Promise<{ id: string; code: string; name: string }[]>,
        systemApi.systemWorkersApi.list() as Promise<{ id: string; role_code: string | null }[]>,
        // P1.2 Phase B — Add Worker defaults. Failures here are
        // non-fatal: an empty defaults object just means no pre-fill,
        // which is the legitimate state for orgs that never
        // configured them.
        systemApi.systemSettingsApi.getWorkers().catch(() => null),
        // P1.2 Phase B item 6 — document types catalog. Filtered to
        // is_required=true below; non-required types stay hidden in
        // the dialog so admins aren't dragged through every upload
        // option for a basic invite.
        organizationApi.documentTypesApi.list().catch(() => []),
      ]);
      // Merge + sort: active first (most relevant for daily admin
      // work), invited at the end so they don't get lost.
      const workers = [
        ...activeWorkers,
        ...invitedWorkers,
      ];
      setWorkers(workers);
      setDepartments(deps);
      setPositions(poss);
      setEmploymentTypes(types);
      setLocations(locs);
      setTimetables(tts);
      setShiftCycles(cycs);
      setRoles(rolesList);
      // P1.2 Phase B — wire the workers settings JSONB into form state.
      // ``http.get`` strips the axios envelope, so the response is the
      // Setting body directly: { id, category, data, updated_at }.
      // We pluck `data` (the JSONB blob) which is typed as
      // WorkersSettingsData. Missing keys → no pre-fill, which is the
      // legitimate state for orgs that never configured them.
      const defaults = (workerDefaultsRes?.data ?? {}) as typeof workerDefaults;
      setWorkerDefaults(defaults);
      // P1.2 Phase B item 6 — store only the required-doc rows so the
      // dialog doesn't waste vertical space on optional types. Each
      // row's display label uses ``description`` when set so admins
      // get the helper text the doc-types editor configured.
      setRequiredDocTypes(
        (docTypesRes ?? [])
          .filter((dt) => dt.is_required === true)
          .map((dt) => ({
            id: dt.id,
            name: dt.name,
            description: dt.description ?? null,
          })),
      );
      setOptionalDocTypes(
        (docTypesRes ?? [])
          .filter((dt) => dt.is_required !== true)
          .map((dt) => ({
            id: dt.id,
            name: dt.name,
            description: dt.description ?? null,
          })),
      );
      const map = new Map<string, string>();
      sysUsers.forEach(u => { if (u.role_code) map.set(u.id, u.role_code); });
      setRoleByWorker(map);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('org:activeWorkers.toast.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    fetchDocSummary();
  }, [fetchDocSummary]);

  // ------------------------------------------------------------------
  // FK lookup maps
  // ------------------------------------------------------------------

  const deptName = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of departments) m.set(d.id, d.name);
    return m;
  }, [departments]);

  const posName = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of positions) m.set(p.id, p.title);
    return m;
  }, [positions]);

  const employmentTypeLabel = useCallback(
    (employmentType: EmploymentType) => {
      const keyByCode: Record<string, string> = {
        full_time: 'org:activeWorkers.employmentTypes.fullTime',
        part_time: 'org:activeWorkers.employmentTypes.partTime',
        contract: 'org:activeWorkers.employmentTypes.contract',
        intern: 'org:activeWorkers.employmentTypes.intern',
      };
      const key = employmentType.code
        ? keyByCode[employmentType.code]
        : undefined;
      return key ? t(key) : employmentType.name;
    },
    [t],
  );

  const roleLabel = useCallback(
    (role: { code: string; name: string }) => {
      const keyByCode: Record<string, string> = {
        worker: 'org:activeWorkers.roles.worker',
        admin: 'org:activeWorkers.roles.admin',
        super_admin: 'org:activeWorkers.roles.superAdmin',
        manager: 'org:activeWorkers.roles.manager',
        hr_manager: 'org:activeWorkers.roles.hrManager',
      };
      const key = keyByCode[role.code];
      return key ? t(key) : role.name;
    },
    [t],
  );

  const typeName = useMemo(() => {
    const m = new Map<string, string>();
    for (const employmentType of employmentTypes) {
      m.set(employmentType.id, employmentTypeLabel(employmentType));
    }
    return m;
  }, [employmentTypeLabel, employmentTypes]);

  // locations are only referenced via the dialog's Select (by id), so no
  // id → name map is needed on this page. FormerWorkers + widget
  // consumers can add one if they start rendering location column.

  const empName = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of workers) m.set(e.id, `${e.first_name} ${e.last_name}`);
    return m;
  }, [workers]);

  // ------------------------------------------------------------------
  // Summary KPIs
  // ------------------------------------------------------------------

  const summaryStats = useMemo(() => {
    const total = workers.length;
    const withDepartment = workers.filter(
      (e) => !!e.primary_department_id,
    ).length;
    const withManager = workers.filter((e) => !!e.manager_id).length;

    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    const newThisMonth = workers.filter((e) => {
      if (!e.hire_date) return false;
      const d = new Date(e.hire_date);
      return d.getMonth() === month && d.getFullYear() === year;
    }).length;

    return { total, withDepartment, withManager, newThisMonth };
  }, [workers]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'total',
        value: summaryStats.total,
        title: t('org:activeWorkers.stats.total'),
        description: t('org:activeWorkers.stats.totalDescription'),
        avatar: {
          icon: <PeopleAltOutlinedIcon />,
          bgcolor: 'primary.light',
          color: 'primary.main',
        },
      },
      {
        id: 'withdept',
        value: summaryStats.withDepartment,
        title: t('org:activeWorkers.stats.withDepartment'),
        description: t('org:activeWorkers.stats.withDepartmentDescription'),
        avatar: {
          icon: <WorkOutlineIcon />,
          bgcolor: 'success.light',
          color: 'success.main',
        },
      },
      {
        id: 'withmanager',
        value: summaryStats.withManager,
        title: t('org:activeWorkers.stats.withManager'),
        description: t('org:activeWorkers.stats.withManagerDescription'),
        avatar: {
          icon: <BadgeOutlinedIcon />,
          bgcolor: 'info.light',
          color: 'info.main',
        },
      },
      {
        id: 'newthismonth',
        value: summaryStats.newThisMonth,
        title: t('org:activeWorkers.stats.newThisMonth'),
        description: t('org:activeWorkers.stats.newThisMonthDescription'),
        avatar: {
          icon: <PersonAddAltIcon />,
          bgcolor: 'warning.light',
          color: 'warning.dark',
        },
      },
      // Shown only when the organization HAS a required type. A "0 overdue" on a
      // tenant that requires nothing is a number about nothing, and §6.1's whole
      // argument is that a signal which is always green teaches people to stop
      // reading it.
      ...((docSummary?.required_type_count ?? 0) > 0
        ? [
            {
              id: 'docsoverdue',
              value: docSummary?.overdue ?? 0,
              title: t('org:activeWorkers.stats.documentsOverdue'),
              description: t(
                'org:activeWorkers.stats.documentsOverdueDescription',
                {
                  pending: docSummary?.pending ?? 0,
                  satisfied: docSummary?.satisfied ?? 0,
                },
              ),
              avatar: {
                icon: <DescriptionOutlinedIcon />,
                bgcolor: (docSummary?.overdue ?? 0) > 0 ? 'error.light' : 'success.light',
                // The colour is the STATE, not decoration: red only when something
                // is actually late. An always-amber compliance number is the same
                // defect as an always-green one.
                color: (docSummary?.overdue ?? 0) > 0 ? 'error.main' : 'success.main',
              },
            },
          ]
        : []),
    ],
    [summaryStats, docSummary, t],
  );

  // ------------------------------------------------------------------
  // Filter + pagination
  // ------------------------------------------------------------------

  const filtered = useMemo(() => {
    // Scope by URL department_id first (strict, drives the chip below
    // the header). The Department dropdown selection layers on top.
    const byUrlDept = deptIdFilter
      ? workers.filter((e) => e.primary_department_id === deptIdFilter)
      : workers;
    const scoped = byUrlDept.filter((e) => {
      if (deptFilter !== 'all' && e.primary_department_id !== deptFilter) return false;
      if (locFilter !== 'all' && e.primary_location_id !== locFilter) return false;
      return true;
    });
    const needle = filterText.trim().toLowerCase();
    if (!needle) return scoped;
    return scoped.filter((e) => {
      const depLabel = (e.primary_department_id &&
        deptName.get(e.primary_department_id)) || '';
      const posLabel = (e.position_id && posName.get(e.position_id)) || '';
      return (
        e.worker_code.toLowerCase().includes(needle) ||
        e.first_name.toLowerCase().includes(needle) ||
        e.last_name.toLowerCase().includes(needle) ||
        (e.email ?? '').toLowerCase().includes(needle) ||
        depLabel.toLowerCase().includes(needle) ||
        posLabel.toLowerCase().includes(needle)
      );
    });
  }, [workers, filterText, deptName, posName, deptIdFilter, deptFilter, locFilter]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // ------------------------------------------------------------------
  // CRUD
  // ------------------------------------------------------------------

  const validateForm = (): boolean => {
    if (!current) return false;
    const errs: Record<string, string> = {};
    const isCreate = !current.id;
    // P1.2: every worker uses the worker app / portal for geo
    // clock-in, which means they must activate via email-set-password.
    // So email is now required for every Add Worker, not just the
    // grant-login flow. The dialog also no longer offers an opt-out
    // checkbox — Grant login access is implicit.
    if (!current.first_name?.trim())
      errs.first_name = t('org:activeWorkers.fields.firstNameRequired');
    if (!current.last_name?.trim())
      errs.last_name = t('org:activeWorkers.fields.lastNameRequired');
    if (!current.email?.trim()) {
      errs.email = t('org:activeWorkers.fields.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(current.email)) {
      errs.email = t('org:activeWorkers.fields.emailInvalid');
    }
    if (!current.hire_date) errs.hire_date = t('org:activeWorkers.fields.hireDateRequired');
    if (isCreate) {
      if (!current.primary_department_id)
        errs.primary_department_id = t('org:activeWorkers.fields.departmentRequired');
      if (!current.position_id)
        errs.position_id = t('org:activeWorkers.fields.positionRequired');
      if (!current.employment_type_id)
        errs.employment_type_id = t('org:activeWorkers.fields.employmentTypeRequired');
      if (!current.primary_location_id)
        errs.primary_location_id = t('org:activeWorkers.fields.locationRequired');
      if (!selectedRoleId) {
        errs.role_id = t('org:activeWorkers.fields.roleRequired');
      }
      // P1.2 Phase B item 6 — required document types MUST have a
      // file picked before submit. Skipping a required type silently
      // (per the original chain) created the bug where admins thought
      // 'is_required=true' was enforced; it wasn't on the client side.
      for (const dt of requiredDocTypes) {
        if (!docFiles[dt.id]) {
          errs[`doc_${dt.id}`] = t('org:activeWorkers.fields.docRequired', { name: dt.name });
        }
      }
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleOpenAddDialog = () => {
    // P1.2 Phase B — pre-fill from /v1/system/settings/workers. Each
    // default is independently optional; we only set the field when
    // the configured ID still exists in the dropdown's catalog (the
    // admin may have deleted the department since they configured
    // it). Falling back gracefully avoids a "department not found"
    // form state that the user can't fix without reopening the
    // dialog.
    const initial: Partial<Worker> = {
      hire_date: new Date().toISOString().slice(0, 10),
      attendance_tracked: true,
    };
    if (
      workerDefaults.default_department_id &&
      departments.some((d) => d.id === workerDefaults.default_department_id)
    ) {
      initial.primary_department_id = workerDefaults.default_department_id;
    }
    if (
      workerDefaults.default_position_id &&
      positions.some((p) => p.id === workerDefaults.default_position_id)
    ) {
      initial.position_id = workerDefaults.default_position_id;
    }
    if (
      workerDefaults.default_location_id &&
      locations.some((l) => l.id === workerDefaults.default_location_id)
    ) {
      initial.primary_location_id = workerDefaults.default_location_id;
    }
    if (
      workerDefaults.default_employment_type_id &&
      employmentTypes.some(
        (t) => t.id === workerDefaults.default_employment_type_id,
      )
    ) {
      initial.employment_type_id = workerDefaults.default_employment_type_id;
    }
    setCurrent(initial);
    setFormErrors({});
    setGrantLogin(false);
    setSelectedRoleId('');
    setInitialRoleId('');
    // Reset doc-file pickers so a previous Add (cancelled or
    // completed) doesn't leak files into the next one.
    setDocFiles({});
    setOpenFormDialog(true);
  };

  const handleOpenEditDialog = (e: Worker) => {
    setCurrent({ ...e });
    setFormErrors({});
    // Pre-seed the role select from the current worker's role.
    const roleCode = roleByWorker.get(e.id);
    const existing = roleCode ? roles.find((r) => r.code === roleCode) : null;
    const existingId = existing?.id ?? '';
    setSelectedRoleId(existingId);
    setInitialRoleId(existingId);
    setGrantLogin(false); // not used in edit mode; role select is the control
    setOpenFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setCurrent(null);
    setFormErrors({});
    setGrantLogin(false);
    setSelectedRoleId('');
    setInitialRoleId('');
  };

  const handleSave = async () => {
    if (!validateForm() || !current) return;
    setSaving(true);
    try {
      if (current.id) {
        // --------------------------------------------------
        // Edit path — update HR fields, then optionally reassign
        // role via systemWorkersApi.update(). We only hit the role API
        // when the admin actually changed the role to avoid noise on
        // the audit log.
        // --------------------------------------------------
        const update: WorkerUpdate = {
          worker_code: current.worker_code ?? null,
          first_name: current.first_name ?? null,
          last_name: current.last_name ?? null,
          email: current.email ?? null,
          phone: current.phone ?? null,
          hire_date: current.hire_date ?? null,
          primary_department_id: current.primary_department_id ?? null,
          position_id: current.position_id ?? null,
          employment_type_id: current.employment_type_id ?? null,
          primary_location_id: current.primary_location_id ?? null,
          manager_id: current.manager_id ?? null,
          status: current.status ?? null,
          attendance_tracked: current.attendance_tracked ?? true,
        };
        await organizationApi.workersApi.update(current.id, update);
        // Role change. Empty selection → use the dedicated Revoke
        // Login button (that's the only path that clears role AND
        // auth_user_id). Setting a non-empty different role calls
        // update which replaces the WorkerRole atomically.
        if (selectedRoleId && selectedRoleId !== initialRoleId) {
          await systemApi.systemWorkersApi.update(current.id, {
            role_id: selectedRoleId,
          });
        }
        setToast(
          t('org:activeWorkers.toast.updated', { name: `${current.first_name} ${current.last_name}` }),
        );
        handleCloseFormDialog();
        await fetchAll();
      } else {
        // --------------------------------------------------
        // Create — P1.2: always route through invite. Every worker
        // needs login access to use the worker app / portal for
        // geo clock-in, so the prior "create without login" path is
        // gone. The dialog enforces email + role + the four
        // org-context fields (department / position / location /
        // employment type) before reaching this branch.
        //
        // Two-call pattern: invite creates the Worker row with
        // status='invited' + role + invite token in one transaction,
        // then we PATCH the HR fields on top. If the patch fails we
        // log + show the invite dialog anyway — the worker exists
        // and can sign in; the missing HR fields are recoverable in
        // edit mode.
        // --------------------------------------------------
        const result = await systemApi.systemWorkersApi.invite({
          email: current.email!.trim(),
          first_name: current.first_name!.trim(),
          last_name: current.last_name!.trim(),
          role_id: selectedRoleId,
        });
        const hrPatch: WorkerUpdate = {};
        if (current.phone) hrPatch.phone = current.phone;
        if (current.hire_date) hrPatch.hire_date = current.hire_date;
        if (current.primary_department_id)
          hrPatch.primary_department_id = current.primary_department_id;
        if (current.position_id) hrPatch.position_id = current.position_id;
        if (current.employment_type_id)
          hrPatch.employment_type_id = current.employment_type_id;
        if (current.primary_location_id)
          hrPatch.primary_location_id = current.primary_location_id;
        if (current.manager_id) hrPatch.manager_id = current.manager_id;
        if (current.worker_code) hrPatch.worker_code = current.worker_code;
        if (current.attendance_tracked === false) hrPatch.attendance_tracked = false;
        if (Object.keys(hrPatch).length > 0) {
          try {
            await organizationApi.workersApi.update(result.worker_id, hrPatch);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Post-invite HR patch failed:', e);
          }
        }
        // P1.2 Phase B item 6 — chain the required-document uploads.
        // Each upload is independent: a failure on one doesn't abort
        // the others, and we only set a snackbar warning at the end
        // listing what failed. The worker row is already created at
        // this point, so admins can finish any failed uploads from
        // the worker's profile if needed.
        const docUploadFailures: string[] = [];
        for (const dt of [...requiredDocTypes, ...optionalDocTypes]) {
          const file = docFiles[dt.id];
          if (!file) continue;
          try {
            await organizationApi.workerDocumentsApi.upload(
              result.worker_id, dt.id, file,
            );
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(`Doc upload failed for ${dt.name}:`, e);
            docUploadFailures.push(dt.name);
          }
        }
        if (docUploadFailures.length > 0) {
          setToast(
            t('org:activeWorkers.toast.documentUploadsPartial', {
              documents: docUploadFailures.join(', '),
            }),
          );
        }
        setInviteResult({
          workerName: `${current.first_name} ${current.last_name}`,
          email: result.email,
          invite_url: result.invite_url,
          expires_at: result.expires_at,
        });
        handleCloseFormDialog();
        await fetchAll();
      }
    } catch (e) {
      // 402 plan-limit reached -> upsell dialog instead of an inline
      // error string. The detail copy from enforce_worker_limit is
      // already actionable on its own (current/max + resource label).
      if (openIfLimit(e)) {
        return;
      }
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:activeWorkers.toast.saveFailed');
      setFormErrors((prev) => ({ ...prev, _global: message }));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (e: Worker) => {
    setToDelete(e);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setToDelete(null);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await organizationApi.workersApi.delete(toDelete.id);
      setSelectedIds((prev) => prev.filter((id) => id !== toDelete.id));
      // Dialog auto-closes on success; still clear row selection.
      handleCloseDeleteDialog();
      setToast(
        t('org:activeWorkers.toast.deleted', { name: `${toDelete.first_name} ${toDelete.last_name}` }),
      );
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:activeWorkers.toast.deleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for retry.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  // Revoke login access for a worker — clears their role + auth_user_id
  // via the backend's DELETE /v1/system/workers/{worker_id}/access endpoint.
  // Keeps the worker record active (unlike Terminate). Called from
  // the "Revoke Login" button in the Edit dialog footer.
  const handleRevokeLogin = async () => {
    if (!pendingRevoke) return;
    setRevokeSaving(true);
    try {
      await systemApi.systemWorkersApi.revokeAccess(pendingRevoke.id);
      setToast(
        t('org:activeWorkers.toast.revokeAccessSuccess', { name: `${pendingRevoke.first_name} ${pendingRevoke.last_name}` }),
      );
      setPendingRevoke(null);
      // Close the edit form too — the role state is now stale.
      handleCloseFormDialog();
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:activeWorkers.toast.revokeAccessFailed');
      setToast(message);
      throw e; // keep ConfirmDialog open for retry
    } finally {
      setRevokeSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      setToast(t('org:activeWorkers.toast.selectOneDelete'));
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    for (const id of selectedIds) {
      try {
        await organizationApi.workersApi.delete(id);
      } catch {
        failures.push(id);
      }
    }
    setSelectedIds([]);
    await fetchAll();
    setSaving(false);
    if (failures.length === 0) {
      setToast(t('org:activeWorkers.toast.bulkDeleted', { count: selectedIds.length }));
    } else {
      setToast(
        t('org:activeWorkers.toast.bulkDeletedPartial', {
          ok: selectedIds.length - failures.length,
          failed: failures.length,
        }),
      );
    }
  };

  const handleBulkTransfer = async () => {
    if (selectedIds.length === 0 || !bulkTransferDept) return;
    setBulkTransferSaving(true);
    try {
      const result = await organizationApi.workersApi.bulkUpdate({
        worker_ids: selectedIds,
        update: { primary_department_id: bulkTransferDept },
      });
      const message = result.failed
        ? t('org:activeWorkers.toast.transferredWithFailed', { count: result.updated, failed: result.failed })
        : t('org:activeWorkers.toast.transferred', { count: result.updated });
      setToast(message);
      setBulkTransferOpen(false);
      setSelectedIds([]);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('org:activeWorkers.toast.saveFailed'));
    } finally {
      setBulkTransferSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // Row kebab-menu actions + attendance-linked dialogs.
  // ------------------------------------------------------------------

  const openRowMenu = (target: Worker, anchor: HTMLElement) => {
    setMenuTarget(target);
    setMenuAnchor(anchor);
  };
  const closeRowMenu = () => {
    setMenuAnchor(null);
    // leave menuTarget set so the dialog opener can read it
  };

  // P1.2: Retry grant — re-issue an invite token + email for a worker
  // who's stuck in 'invited' status (the user accepted the invite but
  // never finished, or the email landed in spam, etc.). Reuses the
  // same systemWorkersApi.invite() endpoint that creates new invites,
  // because the backend invite_worker is idempotent: if a worker with
  // the same email already exists in 'invited' state, it refreshes
  // the role link, regenerates the token, and triggers a fresh email.
  // Surfaces the same one-time invite_url dialog the create flow uses
  // so the admin can copy-paste if the email never arrives.
  // P0.1 follow-up — manually flip a stuck 'invited' worker to
  // 'active'. This is the cleanup path for users who walked through
  // accept-invite but their Worker row never claimed (pre-P0.1
  // deploy or claim_invitation network failure). The backend
  // update_worker_access endpoint also seeds LeaveBalance rows on
  // this transition, so a single click here both activates the
  // worker AND populates their leave entitlements.
  const handleActivateWorker = async () => {
    if (!menuTarget) return;
    closeRowMenu();
    const target = menuTarget;
    try {
      await systemApi.systemWorkersApi.update(target.id, { status: 'active' });
      setToast(
        t('org:activeWorkers.toast.activated', {
          name: `${target.first_name} ${target.last_name}`,
        }),
      );
      await fetchAll();
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'),
      );
    }
  };

  const handleRetryGrant = async () => {
    if (!menuTarget) return;
    closeRowMenu();
    const target = menuTarget;
    if (!target.email) {
      setToast(t('org:activeWorkers.toast.retryNoEmail', { name: target.first_name }));
      return;
    }
    // Find the worker's current role so we send the right role_id
    // through invite_worker. Falls back to the first 'worker'-coded
    // role if nothing's bound yet (shouldn't happen — the invite
    // flow always sets one — but guards a corrupt-state row).
    const currentRoleCode = roleByWorker.get(target.id);
    const role = currentRoleCode
      ? roles.find((r) => r.code === currentRoleCode)
      : roles.find((r) => r.code === 'worker') ?? roles[0];
    if (!role) {
      setToast(t('org:activeWorkers.toast.noRoleAvailable'));
      return;
    }
    try {
      const result = await systemApi.systemWorkersApi.invite({
        email: target.email,
        first_name: target.first_name,
        last_name: target.last_name,
        role_id: role.id,
      });
      setInviteResult({
        workerName: `${target.first_name} ${target.last_name}`,
        email: result.email,
        invite_url: result.invite_url,
        expires_at: result.expires_at,
      });
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('org:activeWorkers.toast.retryFailed'));
    }
  };

  const handleViewAttendance = () => {
    if (!menuTarget) return;
    const query = new URLSearchParams();
    // TimeEntries filters by the `worker` free-text param (name or
    // code). Use name so the URL stays human-readable.
    query.set('worker', `${menuTarget.first_name} ${menuTarget.last_name}`);
    closeRowMenu();
    navigate(`/attendance/time-entries?${query.toString()}`);
  };

  const openManualEntryFor = (worker: Worker) => {
    setManualEmp(worker);
    setManual({
      date: new Date().toISOString().slice(0, 10),
      clock_in_time: '09:00',
      clock_out_time: '',
      notes: '',
    });
    setManualOpen(true);
    closeRowMenu();
  };

  const canSaveManual = useMemo(
    () =>
      !!manualEmp &&
      !!manual.date &&
      !!manual.clock_in_time &&
      (!manual.clock_out_time || manual.clock_out_time > manual.clock_in_time),
    [manualEmp, manual],
  );

  const handleSaveManual = async () => {
    if (!canSaveManual || !manualEmp) return;
    setManualSaving(true);
    try {
      const payload: ManualEntryCreate = {
        worker_id: manualEmp.id,
        date: manual.date,
        clock_in: `${manual.date}T${manual.clock_in_time}:00Z`,
        clock_out: manual.clock_out_time
          ? `${manual.date}T${manual.clock_out_time}:00Z`
          : null,
        notes: manual.notes || null,
      };
      await attendanceApi.timeEntriesApi.manual(payload);
      setToast(t('org:activeWorkers.toast.manualEntrySaved', { name: `${manualEmp.first_name} ${manualEmp.last_name}` }));
      setManualOpen(false);
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('org:activeWorkers.toast.manualEntryFailed'));
    } finally {
      setManualSaving(false);
    }
  };

  const openAssignScheduleFor = (worker: Worker) => {
    setAssignEmp(worker);
    setAssignForm({
      kind: 'fixed',
      timetable_id: '',
      shift_cycle_id: '',
      effective_from: new Date().toISOString().slice(0, 10),
      auto_shift: false,
    });
    setAssignOpen(true);
    closeRowMenu();
  };

  const canSaveAssign = useMemo(
    () =>
      !!assignEmp &&
      ((assignForm.kind === 'fixed' && !!assignForm.timetable_id) ||
        (assignForm.kind === 'cycle' && !!assignForm.shift_cycle_id)) &&
      !!assignForm.effective_from,
    [assignEmp, assignForm],
  );

  const handleSaveAssign = async () => {
    if (!canSaveAssign || !assignEmp) return;
    setAssignSaving(true);
    try {
      const payload: WorkerScheduleAssign = {
        type: assignForm.kind,
        effective_from: assignForm.effective_from,
        auto_shift: assignForm.auto_shift,
        timetable_id: assignForm.kind === 'fixed' ? assignForm.timetable_id : null,
        shift_cycle_id: assignForm.kind === 'cycle' ? assignForm.shift_cycle_id : null,
      };
      await attendanceApi.schedulesApi.assignWorkerSchedule(assignEmp.id, payload);
      setToast(t('org:activeWorkers.toast.scheduleAssigned', { name: `${assignEmp.first_name} ${assignEmp.last_name}` }));
      setAssignOpen(false);
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('org:activeWorkers.toast.scheduleAssignFailed'));
    } finally {
      setAssignSaving(false);
    }
  };

  const openTransferFor = (worker: Worker) => {
    setTransferEmp(worker);
    setTransferDept(worker.primary_department_id ?? '');
    setTransferOpen(true);
    closeRowMenu();
  };

  const handleSaveTransfer = async () => {
    if (!transferEmp || !transferDept || transferDept === transferEmp.primary_department_id) {
      setTransferOpen(false);
      return;
    }
    setTransferSaving(true);
    try {
      await organizationApi.workersApi.update(transferEmp.id, {
        primary_department_id: transferDept,
      });
      setToast(
        t('org:activeWorkers.toast.transferredOne', {
          name: `${transferEmp.first_name} ${transferEmp.last_name}`,
        }),
      );
      setTransferOpen(false);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('org:activeWorkers.toast.transferOneFailed'));
    } finally {
      setTransferSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // Header-level CSV export.
  //
  // ONE FILE (ORG_IMPORT_DESIGN §10). This used to build the CSV in the browser
  // with display NAMES for exactly the FK columns the import needs — so it
  // *almost* round-tripped: edit it, re-upload it, and every FK column failed. A
  // file that almost round-trips is worse than one that obviously does not,
  // because the admin edits it.
  //
  // It also wrote no BOM (a CJK name is mojibake in Excel) and carried a
  // hand-rolled quote escaper. All three go away by asking the backend, which is
  // the same producer the import dialog's "download current" uses — so there is
  // no second writer of this header to drift.
  //
  // The FILTER survives the merge: the ids the page has filtered are passed
  // through, rather than the server re-implementing a client-side filter over
  // department, location and free text.
  // ------------------------------------------------------------------

  const handleExportCSV = async () => {
    if (filtered.length === 0) {
      setToast(t('org:activeWorkers.toast.exportEmpty'));
      return;
    }
    try {
      await organizationApi.workerImportApi.downloadTemplate(false, filtered.map((e) => e.id));
      setToast(
        t('org:activeWorkers.toast.exportDone', { count: filtered.length }),
      );
    } catch (e) {
      setToast(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('common:feedback.errorGeneric'),
      );
    }
  };
  /**
   * Terminate (status='terminated') rather than delete. Keeps the record
   * but moves it off ActiveWorkers and onto FormerWorkers. Preferred
   * over hard-delete for anyone who has historical attendance.
   *
   * termination_date is NOT in the WorkerUpdate schema — the backend
   * sets it automatically when status flips to 'terminated'. Don't try
   * to pass it from the frontend.
   */
  const handleTerminate = async (e: Worker) => {
    setSaving(true);
    try {
      await organizationApi.workersApi.update(e.id, {
        status: 'terminated',
      });
      setToast(t('org:activeWorkers.toast.terminated', { name: `${e.first_name} ${e.last_name}` }));
      await fetchAll();
    } catch (err) {
      const message =
        err instanceof ApiError ? translateApiError(err, t) : t('org:activeWorkers.toast.terminateFailed');
      setToast(message);
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // Form change helpers
  // ------------------------------------------------------------------

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormChange = (event: any) => {
    const { name, value } = event.target;
    setCurrent((prev) => (prev ? { ...prev, [name]: value } : null));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setCurrent((prev) =>
      prev ? { ...prev, [name]: value === '' ? null : value } : null,
    );
  };

  // Manager Autocomplete: exclude self (can't manage self) when editing.
  const managerOptions = useMemo(() => {
    const selfId = current?.id;
    return workers
      .filter((e) => e.id !== selfId)
      .map((e) => ({
        id: e.id,
        label: `${e.first_name} ${e.last_name} (${e.worker_code})`,
      }));
  }, [workers, current?.id]);

  const selectedManager =
    (current?.manager_id &&
      managerOptions.find((o) => o.id === current.manager_id)) ||
    null;

  // ------------------------------------------------------------------
  // Row selection / pagination / filter
  // ------------------------------------------------------------------

  const handleSelectRowClick = (
    _event: React.SyntheticEvent,
    id: string,
  ) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const isSelected = (id: string) => selectedIds.includes(id);

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    setPage(0);
  };
  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedIds(event.target.checked ? filtered.map((e) => e.id) : []);
  };

  // ------------------------------------------------------------------
  // Render guards
  // ------------------------------------------------------------------

  if (loading && workers.length === 0) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error && workers.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchAll}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: '1.125rem' }}
          >
            {t('org:activeWorkers.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:activeWorkers.subtitle')}
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          sx={{ flexShrink: 0, flexWrap: { xs: 'wrap', md: 'nowrap' } }}
        >
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchAll}
              sx={{
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            size="small"
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
            }}
          >
            {t('org:activeWorkers.addButton')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleExportCSV}
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              color: 'text.primary',
              borderColor: 'rgba(0,0,0,0.2)',
              bgcolor: '#ffffff',
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: '#ffffff' },
            }}
          >
            {t('org:activeWorkers.exportButton')}
          </Button>
          {/* The other half of §10's one file: this downloads it, that uploads it.
              Outlined, so the toolbar keeps exactly ONE contained button. */}
          <Button
            variant="outlined"
            onClick={() => setImportOpen(true)}
            size="small"
            startIcon={<FileUploadOutlinedIcon />}
            sx={{
              ml: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              color: 'text.primary',
              borderColor: 'rgba(0,0,0,0.2)',
              bgcolor: '#ffffff',
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: '#ffffff' },
            }}
          >
            {t('org:import.entities.workers.title')}
          </Button>
          {selectedIds.length > 0 && (
            <>
              <Button
                variant="outlined"
                size="small"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={(event) => setBulkMenuAnchor(event.currentTarget)}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  bgcolor: '#ffffff',
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#ffffff' },
                }}
              >
                {t('attendance:approvals.bulkActions', { count: selectedIds.length })}
              </Button>
              <Menu
                anchorEl={bulkMenuAnchor}
                open={Boolean(bulkMenuAnchor)}
                onClose={() => setBulkMenuAnchor(null)}
              >
                <MenuItem
                  disabled={saving}
                  onClick={() => {
                    setBulkMenuAnchor(null);
                    setBulkTransferDept('');
                    setBulkTransferOpen(true);
                  }}
                >
                  <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('org:activeWorkers.bulkTransferButton')}</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem
                  disabled={saving}
                  onClick={() => {
                    setBulkMenuAnchor(null);
                    void handleBulkDelete();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('org:shared.bulkDelete')}</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      </Box>

      {/* Active department filter (via ?department_id URL param) —
          rendered as a dismissable chip above the summary cards. */}
      {deptIdFilter && (
        <Box sx={{ width: '100%', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('org:activeWorkers.filters.filteredToDepartment')}
          </Typography>
          <Chip
            label={deptName.get(deptIdFilter) ?? deptIdFilter.slice(0, 8)}
            onDelete={clearDeptIdFilter}
            color="primary"
            size="small"
            sx={{ borderRadius: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            · {t('org:activeWorkers.filters.workersVisible', { count: filtered.length })}
          </Typography>
        </Box>
      )}

      {/* Compact stats — 13" laptop friendly */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.flatMap((card, i) => [
          i > 0 ? <Typography key={`sep-${card.id}`} variant="caption" color="text.disabled">·</Typography> : null,
          <Typography key={card.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(card.avatar.icon, { sx: { fontSize: 14, color: card.avatar.color } })}
            <strong>{card.value}</strong> {card.title}
          </Typography>,
        ]).filter(Boolean)}
      </Stack>

      {/* Filter bar — FILTER_DESIGN_CONVENTION §3.
          Location (Where) → Department (Who) → Search (Who free-text). */}
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('org:activeWorkers.filters.locationLabel')}</InputLabel>
          <Select value={locFilter} label={t('org:activeWorkers.filters.locationLabel')}
            onChange={(e) => { setLocFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t('org:activeWorkers.filters.allLocations')}</MenuItem>
            {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('org:activeWorkers.filters.departmentLabel')}</InputLabel>
          <Select value={deptFilter} label={t('org:activeWorkers.filters.departmentLabel')}
            onChange={(e) => { setDeptFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t('org:activeWorkers.filters.allDepartments')}</MenuItem>
            {Array.from(deptName.entries()).map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField variant="outlined"
          label={t('org:activeWorkers.searchLabel')}
          value={filterText} onChange={handleFilterChange} size="small"
          sx={{ width: 340, maxWidth: '100%' }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]} component="div"
          count={filtered.length} rowsPerPage={rowsPerPage} page={page}
          onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }}
        />
      </Box>

      {/* Chips §5 */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (locFilter !== 'all') {
          const name = locations.find((l) => l.id === locFilter)?.name ?? locFilter;
          chips.push({ key: 'loc', label: t('org:activeWorkers.filters.locationChip', { name }),
            onClear: () => { setLocFilter('all'); setPage(0); } });
        }
        if (deptFilter !== 'all') {
          const name = deptName.get(deptFilter) ?? deptFilter;
          chips.push({ key: 'dept', label: t('org:activeWorkers.filters.departmentChip', { name }),
            onClear: () => { setDeptFilter('all'); setPage(0); } });
        }
        if (filterText.trim()) {
          chips.push({ key: 'search', label: t('org:activeWorkers.filters.searchChip', { query: filterText.trim() }),
            onClear: () => { setFilterText(''); setPage(0); } });
        }
        if (chips.length === 0) return null;
        return (
          <Box sx={{ width: '100%', display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {chips.map((c) => (
              <Chip key={c.key} label={c.label} size="small" onDelete={c.onClear}
                variant="outlined" color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }} />
            ))}
            <Button size="small"
              onClick={() => { setLocFilter('all'); setDeptFilter('all'); setFilterText(''); setPage(0); }}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', minWidth: 0, px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' } }}>
              {t('org:activeWorkers.filters.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {filtered.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {filterText
              ? t('org:activeWorkers.empty.noMatches')
              : t('org:activeWorkers.empty.noResults')}
          </Typography>
          {!filterText && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{ mt: 2 }}
            >
              {t('org:activeWorkers.addButton')}
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table sx={{ minWidth: 900 }} aria-label={t('org:activeWorkers.tableAria')}>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < filtered.length
                    }
                    checked={
                      filtered.length > 0 &&
                      selectedIds.length === filtered.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>{t('org:activeWorkers.table.code')}</TableCell>
                <TableCell>{t('org:activeWorkers.table.name')}</TableCell>
                <TableCell>{t('org:activeWorkers.table.department')}</TableCell>
                <TableCell>{t('org:activeWorkers.table.position')}</TableCell>
                <TableCell>{t('org:activeWorkers.fields.employmentType')}</TableCell>
                <TableCell>{t('org:activeWorkers.table.role')}</TableCell>
                <TableCell>{t('org:activeWorkers.fields.manager')}</TableCell>
                <TableCell>{t('org:activeWorkers.table.hireDate')}</TableCell>
                <TableCell align="center">{t('org:activeWorkers.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((worker) => {
                const selected = isSelected(worker.id);
                return (
                  <TableRow
                    hover
                    key={worker.id}
                    selected={selected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={(event) => handleSelectRowClick(event, worker.id)}
                        inputProps={{ 'aria-label': `${worker.first_name} ${worker.last_name}` }}
                      />
                    </TableCell>
                    <TableCell>{worker.worker_code}</TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {worker.first_name} {worker.last_name}
                          </Typography>
                          {worker.status === 'invited' && (
                            <Tooltip title={t('org:activeWorkers.form.pendingTooltip')}>
                              <Chip
                                label={t('org:activeWorkers.form.pendingChip')}
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                              />
                            </Tooltip>
                          )}
                          {worker.auth_user_id && (
                            <Tooltip title={t('org:activeWorkers.form.hasLoginTooltip')}>
                              <VpnKeyOutlined sx={{ fontSize: 12, color: 'primary.main', opacity: 0.6 }} />
                            </Tooltip>
                          )}
                        </Box>
                        {worker.email && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {worker.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {worker.primary_department_id
                        ? deptName.get(worker.primary_department_id) ?? '—'
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {worker.position_id
                        ? posName.get(worker.position_id) ?? '—'
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {worker.employment_type_id
                        ? typeName.get(worker.employment_type_id) ?? '—'
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const roleCode = roleByWorker.get(worker.id);
                        if (!roleCode) return <Typography variant="caption" color="text.disabled">—</Typography>;
                        const role = roles.find((candidate) => candidate.code === roleCode);
                        const label = role ? roleLabel(role) : roleCode;
                        const color = roleCode === 'super_admin' ? 'error' : roleCode === 'admin' || roleCode === 'hr_manager' ? 'primary' : roleCode === 'manager' ? 'warning' : 'default';
                        return <Chip label={label} size="small" variant="outlined" color={color as 'error' | 'primary' | 'warning' | 'default'} sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }} />;
                      })()}
                    </TableCell>
                    <TableCell>
                      {worker.manager_id
                        ? empName.get(worker.manager_id) ?? '—'
                        : '—'}
                    </TableCell>
                    <TableCell>{worker.hire_date ?? '—'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('org:activeWorkers.tooltips.edit')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditDialog(worker);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('org:activeWorkers.tooltips.more')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRowMenu(worker, e.currentTarget);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openFormDialog}
        onClose={handleCloseFormDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {current?.id
            ? t('org:activeWorkers.editDialogTitle')
            : t('org:activeWorkers.addDialogTitle')}
        </DialogTitle>
        <DialogContent>
          {formErrors._global && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors._global}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                margin="dense"
                name="worker_code"
                label={t('org:activeWorkers.fields.workerCode')}
                fullWidth
                required
                value={current?.worker_code || ''}
                onChange={handleFormChange}
                error={!!formErrors.worker_code}
                helperText={formErrors.worker_code}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                margin="dense"
                name="first_name"
                label={t('org:activeWorkers.fields.firstName')}
                fullWidth
                required
                value={current?.first_name || ''}
                onChange={handleFormChange}
                error={!!formErrors.first_name}
                helperText={formErrors.first_name}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                margin="dense"
                name="last_name"
                label={t('org:activeWorkers.fields.lastName')}
                fullWidth
                required
                value={current?.last_name || ''}
                onChange={handleFormChange}
                error={!!formErrors.last_name}
                helperText={formErrors.last_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* HANDOFF_2026-05-25 Bug 10 — verify-then-change email pattern.
                  When editing an existing worker, the email is shown
                  read-only with a 'Verify new email' button as the
                  endAdornment of the same TextField shape. This
                  matches PhoneField's visual height + helperText slot
                  exactly so the Email column and Phone column align
                  byte-for-byte (Bug 17 — owner: '跟按钮，和右侧的 phone
                  的输入框在视觉上很丑，不对齐'). */}
              {current?.id ? (
                <TextField
                  margin="dense"
                  name="email_readonly"
                  type="email"
                  label={t('org:activeWorkers.fields.email')}
                  fullWidth
                  value={current?.email || ''}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: -1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setEmailChangeOpen(true)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                            py: 0.25,
                            px: 1.25,
                            minWidth: 0,
                          }}
                        >
                          {t('org:activeWorkers.fields.verifyNewEmail')}
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                  helperText={t('org:activeWorkers.fields.emailChangeHint')}
                />
              ) : (
                <TextField
                  margin="dense"
                  name="email"
                  type="email"
                  label={t('org:activeWorkers.fields.email')}
                  fullWidth
                  required
                  value={current?.email || ''}
                  onChange={handleFormChange}
                  error={!!formErrors.email}
                  helperText={
                    formErrors.email ||
                    t('org:activeWorkers.fields.emailInviteHint')
                  }
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <PhoneField
                value={current?.phone || ''}
                onChange={(value) => handleFormChange({ target: { name: 'phone', value: value || '' } } as never)}
                label={t('org:activeWorkers.fields.phone')}
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DateField
                margin="dense"
                size="medium"
                name="hire_date"
                label={t('org:activeWorkers.fields.hireDate')}
                fullWidth
                required
                value={current?.hire_date || ''}
                onChange={handleFormChange}
                error={!!formErrors.hire_date}
                helperText={formErrors.hire_date}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl
                fullWidth
                margin="dense"
                required={!current?.id}
                error={!!formErrors.primary_department_id}
              >
                <InputLabel id="dept-label">{t('org:activeWorkers.fields.department')}</InputLabel>
                <Select
                  labelId="dept-label"
                  name="primary_department_id"
                  label={t('org:activeWorkers.fields.department')}
                  value={current?.primary_department_id ?? ''}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">
                    <em>{t('org:activeWorkers.fields.noneOption')}</em>
                  </MenuItem>
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.primary_department_id && (
                  <FormHelperText>{formErrors.primary_department_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl
                fullWidth
                margin="dense"
                required={!current?.id}
                error={!!formErrors.position_id}
              >
                <InputLabel id="pos-label">{t('org:activeWorkers.fields.position')}</InputLabel>
                <Select
                  labelId="pos-label"
                  name="position_id"
                  label={t('org:activeWorkers.fields.position')}
                  value={current?.position_id ?? ''}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">
                    <em>{t('org:activeWorkers.fields.noneOption')}</em>
                  </MenuItem>
                  {positions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.title}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.position_id && (
                  <FormHelperText>{formErrors.position_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl
                fullWidth
                margin="dense"
                required={!current?.id}
                error={!!formErrors.employment_type_id}
              >
                <InputLabel id="type-label">{t('org:activeWorkers.fields.employmentType')}</InputLabel>
                <Select
                  labelId="type-label"
                  name="employment_type_id"
                  label={t('org:activeWorkers.fields.employmentType')}
                  value={current?.employment_type_id ?? ''}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">
                    <em>{t('org:activeWorkers.fields.noneOption')}</em>
                  </MenuItem>
                  {employmentTypes.map((employmentType) => (
                    <MenuItem key={employmentType.id} value={employmentType.id}>
                      {employmentTypeLabel(employmentType)}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.employment_type_id && (
                  <FormHelperText>{formErrors.employment_type_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl
                fullWidth
                margin="dense"
                required={!current?.id}
                error={!!formErrors.primary_location_id}
              >
                <InputLabel id="loc-label">{t('org:activeWorkers.fields.primaryLocation')}</InputLabel>
                <Select
                  labelId="loc-label"
                  name="primary_location_id"
                  label={t('org:activeWorkers.fields.primaryLocation')}
                  value={current?.primary_location_id ?? ''}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">
                    <em>{t('org:activeWorkers.fields.noneOption')}</em>
                  </MenuItem>
                  {locations.map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.primary_location_id && (
                  <FormHelperText>{formErrors.primary_location_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={managerOptions}
                value={selectedManager}
                getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onChange={(_e, v) =>
                  setCurrent((prev) =>
                    prev ? { ...prev, manager_id: v?.id ?? null } : null,
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label={t('org:activeWorkers.fields.manager')}
                    placeholder={t('org:activeWorkers.fields.managerPlaceholder')}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={current?.attendance_tracked !== false}
                    onChange={(e) =>
                      setCurrent((prev) =>
                        prev ? { ...prev, attendance_tracked: e.target.checked } : null,
                      )
                    }
                  />
                }
                label={t('org:activeWorkers.fields.attendanceTracked')}
              />
              <FormHelperText sx={{ mt: -0.5, ml: 0 }}>
                {t('org:activeWorkers.fields.attendanceTrackedHelp')}
              </FormHelperText>
            </Grid>
          </Grid>

          {/* Login access section — Role select + Grant Login
              checkbox (create mode only). See HANDOFF §21.3 and
              ARCHITECTURE §8.2: Role is tenant-scoped and lives on
              WorkerRole; granting access also generates an
              invite token (see backend systemWorkersApi.invite). */}
          <Divider sx={{ mt: 2, mb: 1.5 }} />
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 0.4, fontWeight: 600 }}
          >
            {t('org:activeWorkers.fields.loginAccessSection')}
          </Typography>
          {current?.id ? (
            // Edit mode — show role select + Revoke button inline.
            // Empty selection means "no role" (use Revoke to clear).
            <Grid container spacing={2} sx={{ mt: 0 }} alignItems="flex-start">
              <Grid item xs={12} sm={5}>
                <FormControl
                  fullWidth
                  margin="dense"
                  error={!!formErrors.role_id}
                >
                  <InputLabel id="role-label">{t('org:activeWorkers.fields.role')}</InputLabel>
                  <Select
                    labelId="role-label"
                    label={t('org:activeWorkers.fields.role')}
                    value={selectedRoleId}
                    onChange={(e) => {
                      setSelectedRoleId(e.target.value as string);
                      if (formErrors.role_id)
                        setFormErrors((p) => ({ ...p, role_id: '' }));
                    }}
                  >
                    <MenuItem value="">
                      <em>{t('org:activeWorkers.fields.noneOption')}</em>
                    </MenuItem>
                    {roles
                      .filter((r) => r.code !== 'super_admin' || r.id === selectedRoleId)
                      .map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {roleLabel(r)}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.role_id && (
                    <FormHelperText>{formErrors.role_id}</FormHelperText>
                  )}
                  {selectedRoleId !== initialRoleId && (
                    <FormHelperText sx={{ color: 'primary.main' }}>
                      {t('org:activeWorkers.fields.roleUpdateOnSave')}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  {current.auth_user_id ? (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <VpnKeyOutlined
                        sx={{ fontSize: 14, color: 'success.main' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {t('org:activeWorkers.fields.hasLoginAccess')}
                      </Typography>
                    </Box>
                  ) : initialRoleId ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('org:activeWorkers.fields.invitationPending')}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('org:activeWorkers.fields.noLoginAccess')}
                    </Typography>
                  )}
                  {(current.auth_user_id || initialRoleId) && (
                    <Button
                      size="small"
                      color="warning"
                      variant="outlined"
                      startIcon={<VpnKeyOffOutlinedIcon fontSize="small" />}
                      onClick={() =>
                        current.id && current.first_name
                          ? setPendingRevoke(current as Worker)
                          : null
                      }
                      sx={{
                        mt: 0.5,
                        alignSelf: 'flex-start',
                        textTransform: 'none',
                        borderRadius: 1,
                      }}
                    >
                      {t('org:activeWorkers.fields.revokeLoginAccess')}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          ) : (
            // Create mode — P1.2: every new worker is invited (gets
            // login access). The grant-login checkbox is gone; role
            // is always required.
            <Box sx={{ mt: 1 }}>
              <Alert
                severity="info"
                variant="outlined"
                sx={{ mb: 2, fontSize: '0.8rem' }}
              >
                {t('org:activeWorkers.fields.createInfoBanner')}
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    margin="dense"
                    required
                    error={!!formErrors.role_id}
                  >
                    <InputLabel id="invite-role-label">{t('org:activeWorkers.form.roleLabel')}</InputLabel>
                    <Select
                      labelId="invite-role-label"
                      label={t('org:activeWorkers.form.roleLabel')}
                      value={selectedRoleId}
                      onChange={(e) => {
                        setSelectedRoleId(e.target.value as string);
                        if (formErrors.role_id)
                          setFormErrors((p) => ({ ...p, role_id: '' }));
                      }}
                    >
                      {roles
                      .filter((r) => r.code !== 'super_admin' || r.id === selectedRoleId)
                      .map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {roleLabel(r)}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.role_id && (
                      <FormHelperText>{formErrors.role_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* P1.2 Phase B item 6 — Required documents.
              Only shown in Add mode (current.id absent) and only when
              the catalog has at least one is_required=true type. Each
              row is a file picker; a chosen file is uploaded after
              the worker row is created. Skipped types are silently
              ignored — admins can finish them from the worker
              profile page. */}
          {!current?.id && (requiredDocTypes.length + optionalDocTypes.length) > 0 && (
            /* px is FIELD_TEXT_INSET, not `p: 2`: this card's border sits on the
               same content edge as the outlined fields above it, so the eye
               compares the first text INSIDE each box. MUI insets an outlined
               field's text by 14px; 16 here read as a 2px stagger against every
               field label (owner report 2026-08-08). */
            <Box sx={{ mt: 2.5, py: 2, px: `${FIELD_TEXT_INSET}px`, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('org:activeWorkers.form.docsTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('org:activeWorkers.form.docsBody')}
              </Typography>
              {/* CSS Grid (svdy-ui §11.1/§11.4) — label column + action
                  column so every row's 'Choose file' button lines up,
                  fixing the ragged flex-wrap layout. Required rows get a
                  red * ; optional rows a muted 'Optional' tag. */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                  columnGap: 2,
                  rowGap: 1.5,
                  alignItems: 'center',
                }}
              >
                {[
                  ...requiredDocTypes.map((d) => ({ ...d, required: true })),
                  ...optionalDocTypes.map((d) => ({ ...d, required: false })),
                ].map((dt) => {
                  const file = docFiles[dt.id] ?? null;
                  const err = formErrors[`doc_${dt.id}`];
                  return (
                    <React.Fragment key={dt.id}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {dt.name}
                          {dt.required ? (
                            <Box component="span" sx={{ color: 'error.main', ml: 0.5, fontWeight: 700 }}>*</Box>
                          ) : (
                            <Box component="span" sx={{ color: 'text.disabled', ml: 0.75, fontSize: '0.7rem', fontWeight: 400 }}>
                              · {t('org:activeWorkers.form.docOptional')}
                            </Box>
                          )}
                        </Typography>
                        {dt.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {dt.description}
                          </Typography>
                        )}
                        {err && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25 }}>
                            {err}
                          </Typography>
                        )}
                      </Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ justifySelf: { sm: 'end' }, minWidth: 0, maxWidth: '100%' }}
                      >
                        {file && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={file.name}
                          >
                            {file.name}
                          </Typography>
                        )}
                        <Button
                          component="label"
                          variant={file ? 'text' : 'outlined'}
                          size="small"
                          color={err ? 'error' : 'primary'}
                          sx={{ textTransform: 'none', flexShrink: 0, borderRadius: 1 }}
                        >
                          {file ? t('org:activeWorkers.form.changeFile') : t('org:activeWorkers.form.chooseFile')}
                          <input
                            type="file"
                            hidden
                            onChange={(e) => {
                              const f = e.target.files?.[0] ?? null;
                              setDocFiles((prev) => ({ ...prev, [dt.id]: f }));
                              // Clear the required-doc error as soon as a
                              // file is chosen — closes the validation loop.
                              if (f) {
                                setFormErrors((prev) => {
                                  const next = { ...prev };
                                  delete next[`doc_${dt.id}`];
                                  return next;
                                });
                              }
                              e.target.value = '';
                            }}
                          />
                        </Button>
                        {file && (
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() => setDocFiles((prev) => ({ ...prev, [dt.id]: null }))}
                            sx={{ minWidth: 0, px: 0.75, textTransform: 'none', flexShrink: 0 }}
                          >
                            {t('org:activeWorkers.form.clearFile')}
                          </Button>
                        )}
                      </Stack>
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* P1.2 Bug-fix 2026-05-29 — Documents entry in edit mode.
              Owner reported edit dialog had no document affordance,
              forcing admins to remember the separate WorkerDocuments
              page. We now show a dedicated panel that links there.
              Inline upload in edit dialog was rejected as too dialog-
              heavy for an HR form whose primary job is identity /
              role / org-context fields. */}
          {current?.id && (
            /* Same axis as the Add-mode documents card above — see the comment
               there. Both sit beside outlined fields, so both inset by
               FIELD_TEXT_INSET. */
            <Box sx={{ mt: 2.5, py: 2, px: `${FIELD_TEXT_INSET}px`, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('org:activeWorkers.form.editDocsTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('org:activeWorkers.form.editDocsBody')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  if (current?.id) setDocsWorker(current as Worker);
                }}
                sx={{ textTransform: 'none', borderRadius: 1 }}
              >
                {t('org:activeWorkers.form.openDocsButton')}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ py: '16px' }}>
          <Button onClick={handleCloseFormDialog} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} />
            ) : current?.id ? (
              t('org:shared.saveChanges')
            ) : (
              t('org:activeWorkers.createSubmit')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Row action kebab menu — hosts cross-module shortcuts. */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuTarget?.status === 'invited' && (
          <MenuItem onClick={handleRetryGrant} sx={{ color: 'primary.main' }}>
            <ListItemIcon><SendOutlinedIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText
              primary={t('org:activeWorkers.menu.resendInvitationPrimary')}
              secondary={t('org:activeWorkers.menu.resendInvitationSecondary')}
            />
          </MenuItem>
        )}
        {menuTarget?.status === 'invited' && (
          <MenuItem onClick={handleActivateWorker} sx={{ color: 'success.main' }}>
            <ListItemIcon><HowToRegOutlinedIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText
              primary={t('org:activeWorkers.menu.activatePrimary')}
              secondary={t('org:activeWorkers.menu.activateSecondary')}
            />
          </MenuItem>
        )}
        <MenuItem onClick={handleViewAttendance}>
          <ListItemIcon><VisibilityOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={t('org:activeWorkers.menu.viewAttendancePrimary')}
            secondary={t('org:activeWorkers.menu.viewAttendanceSecondary')}
          />
        </MenuItem>
        <MenuItem onClick={() => { if (menuTarget) setDocsWorker(menuTarget); closeRowMenu(); }}>
          <ListItemIcon><DescriptionOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={t('org:activeWorkers.menu.documentsPrimary')}
            secondary={t('org:activeWorkers.menu.documentsSecondary')}
          />
        </MenuItem>
        <MenuItem onClick={() => menuTarget && openManualEntryFor(menuTarget)}>
          <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={t('org:activeWorkers.menu.manualEntryPrimary')}
            secondary={t('org:activeWorkers.menu.manualEntrySecondary')}
          />
        </MenuItem>
        <MenuItem onClick={() => menuTarget && openAssignScheduleFor(menuTarget)}>
          <ListItemIcon><EventNoteOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={t('org:activeWorkers.menu.assignSchedulePrimary')}
            secondary={t('org:activeWorkers.menu.assignScheduleSecondary')}
          />
        </MenuItem>
        <MenuItem onClick={() => menuTarget && openTransferFor(menuTarget)}>
          <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('org:activeWorkers.menu.transferDepartmentPrimary')} />
        </MenuItem>
        <MenuItem
          onClick={() => { if (menuTarget) setPendingTerminate(menuTarget); closeRowMenu(); }}
          sx={{ color: 'warning.dark' }}
        >
          <ListItemIcon><PersonAddAltIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} /></ListItemIcon>
          <ListItemText
            primary={t('org:activeWorkers.menu.endEmploymentPrimary')}
            secondary={t('org:activeWorkers.menu.endEmploymentSecondary')}
          />
        </MenuItem>
        <MenuItem
          onClick={() => { if (menuTarget) handleOpenDeleteDialog(menuTarget); closeRowMenu(); }}
          // ADR-049 rule 2. The backend REFUSES to delete a record while employment is live, so
          // offering an enabled button here would be a click that always fails. Disabled rather
          // than hidden: the capability stays discoverable and the secondary line names the next
          // step, which is the Terminate item directly above (svdy-ui §19).
          disabled={!DELETABLE_STATUSES.includes(menuTarget?.status ?? '')}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText
            primary={t('org:activeWorkers.menu.deletePermanentlyPrimary')}
            secondary={
              DELETABLE_STATUSES.includes(menuTarget?.status ?? '')
                ? t('org:activeWorkers.menu.deletePermanentlySecondary')
                : t('org:activeWorkers.menu.deleteNeedsTerminationFirst')
            }
          />
        </MenuItem>
      </Menu>

      {/* Manual time entry dialog — scoped to the selected worker. */}
      <Dialog open={manualOpen} onClose={() => !manualSaving && setManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('org:activeWorkers.manualEntry.title')}{manualEmp ? t('org:activeWorkers.manualEntry.titleSuffix', { name: `${manualEmp.first_name} ${manualEmp.last_name}` }) : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('org:activeWorkers.manualEntry.subtitleAdmin')}
            </Typography>
            <DateField label={t('org:activeWorkers.manualEntry.dateLabel')} size="small" required
              value={manual.date}
              onChange={(e) => setManual((m) => ({ ...m, date: e.target.value }))} />
            <Stack direction="row" spacing={2}>
              <TextField type="time" label={t('org:activeWorkers.manualEntry.clockIn')} size="small" required
                InputLabelProps={{ shrink: true }}
                value={manual.clock_in_time}
                onChange={(e) => setManual((m) => ({ ...m, clock_in_time: e.target.value }))}
                sx={{ flex: 1 }} />
              <TextField type="time" label={t('org:activeWorkers.manualEntry.clockOut')} size="small"
                InputLabelProps={{ shrink: true }}
                value={manual.clock_out_time}
                onChange={(e) => setManual((m) => ({ ...m, clock_out_time: e.target.value }))}
                sx={{ flex: 1 }} />
            </Stack>
            {manual.clock_out_time && manual.clock_out_time <= manual.clock_in_time && (
              <Alert severity="warning" variant="outlined" sx={{ fontSize: '0.75rem' }}>
                {t('org:activeWorkers.manualEntry.clockOutBeforeWarning')}
              </Alert>
            )}
            <TextField label={t('org:activeWorkers.manualEntry.notes')} multiline rows={2} size="small" fullWidth
              placeholder={t('org:activeWorkers.manualEntry.notesPlaceholder')}
              value={manual.notes}
              onChange={(e) => setManual((m) => ({ ...m, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)} disabled={manualSaving}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveManual} disabled={!canSaveManual || manualSaving}>
            {manualSaving ? <CircularProgress size={18} /> : t('org:activeWorkers.manualEntry.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign schedule override dialog — PUT /schedules/workers/{id} */}
      <Dialog open={assignOpen} onClose={() => !assignSaving && setAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t('org:activeWorkers.assignSchedule.title')}{assignEmp ? t('org:activeWorkers.assignSchedule.titleSuffix', { name: `${assignEmp.first_name} ${assignEmp.last_name}` }) : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('org:activeWorkers.assignSchedule.subtitleAdmin')}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>{t('org:activeWorkers.assignSchedule.kindLabel')}</InputLabel>
              <Select value={assignForm.kind} label={t('org:activeWorkers.assignSchedule.kindLabel')}
                onChange={(e) => setAssignForm((p) => ({ ...p, kind: e.target.value as 'fixed' | 'cycle' }))}>
                <MenuItem value="fixed">{t('org:activeWorkers.assignSchedule.kindFixed')}</MenuItem>
                <MenuItem value="cycle">{t('org:activeWorkers.assignSchedule.kindCycle')}</MenuItem>
              </Select>
            </FormControl>
            {assignForm.kind === 'fixed' ? (
              <FormControl fullWidth size="small" disabled={timetables.length === 0}>
                <InputLabel>{t('org:activeWorkers.assignSchedule.timetable')}</InputLabel>
                <Select value={assignForm.timetable_id} label={t('org:activeWorkers.assignSchedule.timetable')}
                  onChange={(e) => setAssignForm((p) => ({ ...p, timetable_id: e.target.value }))}>
                  {timetables.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth size="small" disabled={shiftCycles.length === 0}>
                <InputLabel>{t('org:activeWorkers.assignSchedule.shiftCycle')}</InputLabel>
                <Select value={assignForm.shift_cycle_id} label={t('org:activeWorkers.assignSchedule.shiftCycle')}
                  onChange={(e) => setAssignForm((p) => ({ ...p, shift_cycle_id: e.target.value }))}>
                  {shiftCycles.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <DateField label={t('org:activeWorkers.assignSchedule.effectiveFrom')} size="small" required
              value={assignForm.effective_from}
              onChange={(e) => setAssignForm((p) => ({ ...p, effective_from: e.target.value }))} />
            {(assignForm.kind === 'fixed' && timetables.length === 0) && (
              <Alert severity="info" variant="outlined" sx={{ fontSize: '0.75rem' }}>
                {t('org:activeWorkers.assignSchedule.noTimetablesHint')}
              </Alert>
            )}
            {(assignForm.kind === 'cycle' && shiftCycles.length === 0) && (
              <Alert severity="info" variant="outlined" sx={{ fontSize: '0.75rem' }}>
                {t('org:activeWorkers.assignSchedule.noShiftCyclesHint')}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} disabled={assignSaving}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveAssign} disabled={!canSaveAssign || assignSaving}>
            {assignSaving ? <CircularProgress size={18} /> : t('org:activeWorkers.assignSchedule.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer department dialog — PATCH workers/{id}. */}
      <Dialog open={transferOpen} onClose={() => !transferSaving && setTransferOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t('org:activeWorkers.transfer.title')}{transferEmp ? t('org:activeWorkers.transfer.titleSuffix', { name: `${transferEmp.first_name} ${transferEmp.last_name}` }) : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('org:activeWorkers.transfer.subtitleAdmin')}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>{t('org:activeWorkers.transfer.label')}</InputLabel>
              <Select value={transferDept} label={t('org:activeWorkers.transfer.label')}
                onChange={(e) => setTransferDept(e.target.value)}>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}
                    disabled={d.id === transferEmp?.primary_department_id}>
                    {d.name}{d.id === transferEmp?.primary_department_id ? t('org:activeWorkers.transfer.currentSuffix') : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)} disabled={transferSaving}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveTransfer}
            disabled={!transferDept || transferDept === transferEmp?.primary_department_id || transferSaving}>
            {transferSaving ? <CircularProgress size={18} /> : t('org:activeWorkers.transfer.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Transfer Dialog */}
      <Dialog open={bulkTransferOpen} onClose={() => !bulkTransferSaving && setBulkTransferOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {t('org:activeWorkers.bulkTransfer.titleHeader', { count: selectedIds.length })}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={2}>
            <Typography variant="caption" color="text.secondary">
              {t('org:activeWorkers.bulkTransfer.body')}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>{t('org:activeWorkers.bulkTransfer.label')}</InputLabel>
              <Select value={bulkTransferDept} label={t('org:activeWorkers.bulkTransfer.label')}
                onChange={(e) => setBulkTransferDept(e.target.value)}>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setBulkTransferOpen(false)} disabled={bulkTransferSaving} size="small" sx={{ borderRadius: 1, textTransform: 'none' }}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={handleBulkTransfer} size="small"
            disabled={!bulkTransferDept || bulkTransferSaving}
            sx={{ borderRadius: 1, textTransform: 'none' }}>
            {bulkTransferSaving ? t('org:activeWorkers.bulkTransfer.submitting') : t('org:activeWorkers.bulkTransfer.submit', { count: selectedIds.length })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* HANDOFF_2026-05-25 Bug 10 — verify-then-change email dialog.
          Surfaces the professional pattern (Stripe / Google Workspace)
          where admin requests a change but the worker confirms via a
          link to the new address. Backend implementation deferred to
          NEXT_SESSION; for now this dialog explains the flow and
          collects the new email but submits to a placeholder. */}
      <Dialog
        open={emailChangeOpen}
        onClose={() => {
          if (emailChangeSubmitting) return;  // can't close mid-submit
          setEmailChangeOpen(false);
          setEmailChangeNew('');
          setEmailChangeError(null);
          setEmailChangeSuccess(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {emailChangeSuccess ? t('org:activeWorkers.emailChange.successTitle') : t('org:activeWorkers.emailChange.title')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {emailChangeSuccess ? (
            <>
              <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                  {t('org:activeWorkers.emailChange.successAlert', { email: emailChangeNew })}
                </Typography>
              </Alert>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 1.5 }}>
                {t('org:activeWorkers.emailChange.successBody', {
                  firstName: current?.first_name || 'The worker',
                  currentEmail: current?.email || '—',
                })}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                {t('org:activeWorkers.emailChange.successFootnote', { firstName: current?.first_name || 'The worker' })}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2 }}>
                {t('org:activeWorkers.emailChange.body', { currentEmail: current?.email || '—' })}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                type="email"
                margin="dense"
                label={t('org:activeWorkers.emailChange.newEmailLabel')}
                placeholder={t('org:activeWorkers.emailChange.newEmailPlaceholder')}
                value={emailChangeNew}
                onChange={(e) => {
                  setEmailChangeNew(e.target.value);
                  if (emailChangeError) setEmailChangeError(null);
                }}
                error={!!emailChangeError}
                helperText={emailChangeError}
                disabled={emailChangeSubmitting}
              />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 1, display: 'block' }}>
                {t('org:activeWorkers.emailChange.footnote')}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => {
              setEmailChangeOpen(false);
              setEmailChangeNew('');
              setEmailChangeError(null);
              setEmailChangeSuccess(false);
            }}
            disabled={emailChangeSubmitting}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {emailChangeSuccess ? t('org:activeWorkers.emailChange.done') : t('common:actions.cancel')}
          </Button>
          {!emailChangeSuccess && (
            <Button
              variant="contained"
              disabled={emailChangeSubmitting || !emailChangeNew.trim()}
              onClick={async () => {
                if (!current?.id) return;
                const trimmed = emailChangeNew.trim();
                if (!trimmed) {
                  setEmailChangeError(t('org:activeWorkers.emailChange.errorRequired'));
                  return;
                }
                if (trimmed.toLowerCase() === (current.email || '').toLowerCase()) {
                  setEmailChangeError(t('org:activeWorkers.emailChange.errorSameEmail'));
                  return;
                }
                setEmailChangeSubmitting(true);
                setEmailChangeError(null);
                try {
                  await organizationApi.workersApi.requestEmailChange(
                    current.id,
                    trimmed,
                  );
                  setEmailChangeSuccess(true);
                } catch (e) {
                  const message =
                    e instanceof ApiError
                      ? translateApiError(e, t)
                      : t('org:activeWorkers.emailChange.errorFailed');
                  setEmailChangeError(message);
                } finally {
                  setEmailChangeSubmitting(false);
                }
              }}
              sx={{ borderRadius: 1, textTransform: 'none' }}
            >
              {emailChangeSubmitting ? t('org:activeWorkers.emailChange.submitting') : t('org:activeWorkers.emailChange.submit')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation — shared ConfirmDialog primitive. */}
      <ConfirmDialog
        open={openDeleteDialog}
        tone="danger"
        title={t('org:activeWorkers.deleteTitle')}
        description={t('org:activeWorkers.deleteDescription')}
        subject={
          toDelete && (
            <>
              <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                {toDelete.first_name} {toDelete.last_name}
              </Box>
              <Box
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {toDelete.worker_code}
                {toDelete.email ? ` · ${toDelete.email}` : ''}
              </Box>
            </>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete}
        onClose={handleCloseDeleteDialog}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>

      {/* Terminate confirmation — replaces the old window.confirm() call.
          Warning tone (not danger) because terminate is reversible via
          the Reinstate action on Former Workers; it's an employment-
          status change, not a data wipe. */}
      <ConfirmDialog
        open={!!pendingTerminate}
        tone="warning"
        title={t('org:activeWorkers.terminateTitle')}
        description={t('org:activeWorkers.terminateDescription')}
        subject={
          pendingTerminate && (
            <>
              <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                {pendingTerminate.first_name} {pendingTerminate.last_name}
              </Box>
              <Box
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {pendingTerminate.worker_code}
                {pendingTerminate.email ? ` · ${pendingTerminate.email}` : ''}
              </Box>
            </>
          )
        }
        confirmLabel={t('org:activeWorkers.terminateConfirm')}
        confirmLoadingLabel={t('org:activeWorkers.terminateLoading')}
        onConfirm={() =>
          pendingTerminate
            ? handleTerminate(pendingTerminate)
            : Promise.resolve()
        }
        onClose={() => setPendingTerminate(null)}
      />

      <ConfirmDialog
        open={!!pendingRevoke}
        tone="warning"
        title={t('org:activeWorkers.revokeTitle')}
        description={t('org:activeWorkers.revokeDescription')}
        subject={
          pendingRevoke && (
            <>
              <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                {pendingRevoke.first_name} {pendingRevoke.last_name}
              </Box>
              <Box
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {pendingRevoke.worker_code}
                {pendingRevoke.email ? ` · ${pendingRevoke.email}` : ''}
              </Box>
            </>
          )
        }
        confirmLabel={t('org:activeWorkers.revokeConfirm')}
        confirmLoadingLabel={t('org:activeWorkers.revokeLoading')}
        onConfirm={handleRevokeLogin}
        onClose={() => !revokeSaving && setPendingRevoke(null)}
      />

      {/* Invite-sent confirmation — switched 2026-05-22 from a modal
          dialog to a non-blocking Snackbar (svdy-ui §13). The dialog
          framed copy-link as the primary action, which:
            (a) makes admins normalize sharing the token through chat
                tools (privacy leak via clipboard / screenshots /
                history);
            (b) implies the email might not work, undermining the
                automatic delivery the backend already does.
          The new pattern matches Linear / Notion / Slack: lead with
          reassurance ("Invite sent to <email>"), and surface the
          copy-link as a secondary action only when the admin opens
          the Snackbar's affordance. The full recovery path (resend /
          cancel) lives on the row kebab menu (the 'Retry grant' item)
          which is the right place for asynchronous re-actions. */}
      <Snackbar
        open={!!inviteResult}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setInviteResult(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setInviteResult(null)}
          action={
            inviteResult ? (
              <Button
                size="small"
                color="inherit"
                startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
                onClick={async () => {
                  if (!inviteResult) return;
                  try {
                    await navigator.clipboard.writeText(inviteResult.invite_url);
                    setToast(
                      t('org:activeWorkers.inviteSnackbar.linkCopied'),
                    );
                  } catch {
                    setToast(t('org:activeWorkers.inviteSnackbar.copyFailed'));
                  }
                  setInviteResult(null);
                }}
                sx={{ textTransform: 'none' }}
              >
                {t('org:activeWorkers.inviteSnackbar.copyLink')}
              </Button>
            ) : null
          }
          sx={{ width: '100%', maxWidth: 520 }}
        >
          {inviteResult && (
            <>
              {t('org:activeWorkers.inviteSnackbar.sentTo', { email: inviteResult.email })}{' '}
              <Box component="span" sx={{ opacity: 0.85, fontSize: '0.8rem' }}>
                {t('org:activeWorkers.inviteSnackbar.willReceive', { name: inviteResult.workerName })}
                {inviteResult.expires_at &&
                  t('org:activeWorkers.inviteSnackbar.expires', {
                    date: new Date(inviteResult.expires_at).toLocaleDateString(activeLocale(), {
                      month: 'short',
                      day: 'numeric',
                    }),
                  })}
                .
              </Box>
            </>
          )}
        </Alert>
      </Snackbar>

      {/* Phase C — limit-reached upsell. Backend's enforce_worker_limit
          returns 402 when the org has hit max_workers; this dialog
          turns that hard stop into a clear path to /system/billing. */}
      <UpsellDialog
        open={upsell.open}
        message={upsell.message}
        onClose={closeUpsell}
      />

      {/* Per-worker documents — view / download / upload / remove. */}
      <WorkerDocumentsDialog
        open={!!docsWorker}
        worker={docsWorker}
        documentTypes={docTypeCatalog}
        onClose={() => setDocsWorker(null)}
      />

      {/* `entity="workers"` — ONE dialog for every sheet (see ImportDialog). */}
      <ImportDialog
        open={importOpen}
        entity="workers"
        onClose={() => setImportOpen(false)}
        onApplied={fetchAll}
      />
    </Box>
  );
};

export default ActiveWorkers;
